import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import MainNavigation from '../../components/ui/MainNavigation';

const sections = [
  {
    id: 1,
    title: "1. Definities en toepasselijkheid",
    content: (
      <div className="space-y-3">
        <p>
          Deze voorwaarden gelden voor het gebruik van Verduurzaamwijzer.nu en voor informatie- en adviesaanvragen
          via het platform.
        </p>
        <ul className="space-y-2 list-none">
          {[
            ["Platform", "de website en de daarop aangeboden informatie- en bemiddelingsfunctionaliteit."],
            ["Gebruiker", "iedere bezoeker of aanvrager."],
            ["Partner", "een zelfstandige leverancier, adviseur of installateur die via het platform in contact kan komen met gebruikers."],
          ]?.map(([label, desc], i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-1 w-2 h-2 rounded-full bg-primary flex-shrink-0" />
              <span><strong>{label}:</strong> {desc}</span>
            </li>
          ))}
        </ul>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <tbody>
              {[
                ["Exploitant", "De Content Factory BV"],
                ["Handelsnaam", "Verduurzaamwijzer.nu"],
                ["Vestigingsadres", "Oude Middenweg 17, 2491 AC Den Haag"],
                ["KvK-nummer", "84512490"],
                ["Btw-nummer", "8632.40.100.B.01."],
                ["E-mail", "contact@verduurzaamwijzer.nu"],
                ["Telefoonnummer", "06 4765 0537"],
              ]?.map(([label, value], i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-muted/40" : ""}>
                  <td className="py-2 px-4 font-medium text-foreground/80 w-1/2 border border-border/40">{label}</td>
                  <td className="py-2 px-4 text-foreground/70 border border-border/40">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ),
  },
  {
    id: 2,
    title: "2. Aard van de dienstverlening",
    content: (
      <div className="space-y-3">
        <p>
          Verduurzaamwijzer.nu is een onafhankelijk informatie- en adviesplatform voor huiseigenaren. Het platform
          selecteert en beveelt verduurzamingsoplossingen aan op basis van eigen kennis, ervaring en professionele
          beoordeling. Aanbevelingen vormen geen uitputtende vergelijking van de gehele markt.
        </p>
        <p>
          Adviezen zijn afhankelijk van de door de gebruiker verstrekte informatie en zijn bedoeld als ondersteuning
          bij een keuze. Tenzij uitdrukkelijk anders overeengekomen, verricht het platform zelf geen installatie,
          levering of bouwkundige inspectie.
        </p>
      </div>
    ),
  },
  {
    id: 3,
    title: "3. Geen volledige marktvergelijking",
    content: (
      <div className="space-y-3">
        <p>
          De selectie is gebaseerd op eigen beoordelingscriteria, waaronder geschiktheid voor de woning, kwaliteit,
          veiligheid, prestaties, duurzaamheid, service en prijs-kwaliteitverhouding. Niet alle beschikbare
          aanbieders of producten worden onderzocht.
        </p>
        <p className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
          Een aanbeveling is geen garantie dat een product voor iedere gebruiker de goedkoopste of beste keuze is.
        </p>
      </div>
    ),
  },
  {
    id: 4,
    title: "4. Informatie en adviesaanvragen",
    content: (
      <div className="space-y-3">
        <p>
          De gebruiker verstrekt naar beste weten juiste en actuele informatie. De uitkomst van een advies of
          indicatie kan wijzigen wanneer technische gegevens, plaatselijke omstandigheden of wettelijke eisen
          anders blijken.
        </p>
        <p>
          Het indienen van een aanvraag verplicht de gebruiker niet automatisch tot aankoop. Het platform mag om
          aanvullende informatie vragen voordat een aanvraag wordt behandeld.
        </p>
      </div>
    ),
  },
  {
    id: 5,
    title: "5. Partners, offertes en overeenkomsten",
    content: (
      <div className="space-y-3">
        <p>
          Indien een gebruiker met een externe partner een overeenkomst sluit, ontstaat die overeenkomst in beginsel
          rechtstreeks tussen gebruiker en partner. De partner is verantwoordelijk voor eigen offertes,
          productinformatie, levering, installatie, garanties, vergunningen en wettelijke consumentenrechten.
        </p>
        <p>
          Verduurzaamwijzer.nu is niet automatisch contractspartij of uitvoerder. Indien het platform zelf een
          product of dienst verkoopt, moeten daarvoor aanvullende, specifiek toepasselijke verkoopvoorwaarden
          worden verstrekt.
        </p>
      </div>
    ),
  },
  {
    id: 6,
    title: "6. Prijzen, subsidies en besparingen",
    content: (
      <div className="space-y-3">
        <p>
          Genoemde prijzen, terugverdientijden, energiebesparingen en subsidiebedragen zijn indicatief, tenzij
          expliciet anders vermeld. Werkelijke resultaten hangen onder meer af van woningkenmerken, gebruik,
          tarieven, installatiekosten, subsidieregels en technische prestaties.
        </p>
        <p className="text-sm bg-primary/5 border border-primary/20 rounded-lg p-3 text-foreground/70">
          Controleer actuele subsidievoorwaarden bij de bevoegde instantie en vraag een gespecificeerde offerte
          bij de uitvoerende partij.
        </p>
      </div>
    ),
  },
  {
    id: 7,
    title: "7. Commerciële relaties en transparantie",
    content: (
      <div className="space-y-3">
        <p>
          Het platform kan samenwerken met geselecteerde leveranciers en daarvoor een vergoeding ontvangen. Zulke
          relaties worden toegelicht in de transparantieverklaring.
        </p>
        <p>
          De exploitant streeft naar aanbevelingen op basis van inhoudelijke geschiktheid en maakt duidelijk
          wanneer de selectie niet de volledige markt bestrijkt.
        </p>
      </div>
    ),
  },
  {
    id: 8,
    title: "8. Gebruik van de website en intellectuele eigendom",
    content: (
      <div className="space-y-3">
        <p>
          De gebruiker mag de website voor persoonlijke, rechtmatige doeleinden raadplegen. Teksten, afbeeldingen,
          merken, ontwerpen en andere inhoud mogen niet zonder toestemming commercieel worden hergebruikt, tenzij
          de wet dit toestaat of rechten bij derden berusten.
        </p>
        <p className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
          Misbruik, geautomatiseerde overbelasting en ongeoorloofde toegang zijn verboden.
        </p>
      </div>
    ),
  },
  {
    id: 9,
    title: "9. Beschikbaarheid en externe verwijzingen",
    content: (
      <div className="space-y-3">
        <p>
          Wij streven naar actuele en beschikbare informatie, maar kunnen ononderbroken werking of foutloosheid
          niet garanderen. Links naar externe websites worden aangeboden ter informatie.
        </p>
        <p>
          Op websites en diensten van derden gelden hun eigen voorwaarden en privacyregels.
        </p>
      </div>
    ),
  },
  {
    id: 10,
    title: "10. Aansprakelijkheid",
    content: (
      <div className="space-y-3">
        <p>
          Wij zijn verantwoordelijk voor schade voor zover die volgens toepasselijk recht aan ons kan worden
          toegerekend. Wij sluiten aansprakelijkheid niet uit waar dat wettelijk niet is toegestaan, waaronder
          bij opzet of bewuste roekeloosheid en voor dwingendrechtelijke consumentenrechten.
        </p>
        <p>
          Voor fouten of tekortkomingen van zelfstandige partners zijn wij niet zonder meer aansprakelijk; dit
          hangt af van onze eigen rol en wettelijke verplichtingen.
        </p>
      </div>
    ),
  },
  {
    id: 11,
    title: "11. Privacy en klachten",
    content: (
      <div className="space-y-3">
        <p>
          Persoonsgegevens worden verwerkt overeenkomstig de{' '}
          <Link to="/privacy" className="text-primary hover:underline">
            privacyverklaring
          </Link>
          . Vragen of klachten kunnen worden gestuurd naar{' '}
          <a href="mailto:contact@verduurzaamwijzer.nu" className="text-primary hover:underline">
            contact@verduurzaamwijzer.nu
          </a>
          . Wij proberen klachten binnen een redelijke termijn inhoudelijk te beantwoorden.
        </p>
        <p className="text-sm text-foreground/70">
          Deze bepaling beperkt wettelijke klachten- en geschilrechten niet.
        </p>
      </div>
    ),
  },
  {
    id: 12,
    title: "12. Toepasselijk recht en wijzigingen",
    content: (
      <div className="space-y-3">
        <p>
          Op deze voorwaarden is Nederlands recht van toepassing, zonder afbreuk te doen aan dwingende
          consumentenbescherming die anders van toepassing kan zijn. Geschillen kunnen worden voorgelegd aan
          de volgens de wet bevoegde rechter.
        </p>
        <p>
          Wij kunnen voorwaarden voor toekomstig gebruik wijzigen; de actuele versie wordt gepubliceerd met
          ingangsdatum op deze pagina.
        </p>
      </div>
    ),
  },
];

const VoorwaardenPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />
      {/* Hero */}
      <section className="bg-secondary text-secondary-foreground pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 text-sm text-secondary-foreground/60 mb-4">
              <Link to="/homepage" className="hover:text-secondary-foreground/80 transition-colors">Home</Link>
              <Icon name="ChevronRight" size={14} />
              <span>Algemene voorwaarden</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-secondary-foreground mb-3">
              Algemene voorwaarden
            </h1>
            <p className="text-secondary-foreground/70 text-base mb-6">
              De gebruiksvoorwaarden voor Verduurzaamwijzer.nu en informatie- en adviesaanvragen via het platform
            </p>
            <div className="flex flex-wrap gap-4 text-sm text-secondary-foreground/60">
              <span className="flex items-center gap-1.5">
                <Icon name="FileText" size={14} />
                Versie 1.0
              </span>
              <span className="flex items-center gap-1.5">
                <Icon name="Calendar" size={14} />
                9 oktober 2026
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Table of Contents — sticky sidebar */}
            <aside className="lg:w-56 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-3">Inhoud</h2>
                <nav className="space-y-1">
                  {sections?.map((s) => (
                    <a
                      key={s?.id}
                      href={`#section-${s?.id}`}
                      className="block text-sm text-foreground/60 hover:text-primary transition-colors py-0.5 leading-snug"
                    >
                      {s?.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main content */}
            <main className="flex-1 min-w-0">
              <div className="space-y-10">
                {sections?.map((section, index) => (
                  <motion.div
                    key={section?.id}
                    id={`section-${section?.id}`}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.4, delay: index * 0.03 }}
                    className="scroll-mt-24"
                  >
                    <h2 className="font-heading text-lg font-semibold text-foreground mb-3 pb-2 border-b border-border/50">
                      {section?.title}
                    </h2>
                    <div className="text-foreground/75 leading-relaxed text-sm sm:text-base">
                      {section?.content}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Contact card */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="mt-12 bg-primary/5 border border-primary/20 rounded-xl p-6"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Mail" size={18} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-1">Vragen over de voorwaarden?</h3>
                    <p className="text-sm text-foreground/70 mb-3">
                      Neem contact op met De Content Factory BV via{' '}
                      <a href="mailto:contact@verduurzaamwijzer.nu" className="text-primary hover:underline font-medium">
                        contact@verduurzaamwijzer.nu
                      </a>{' '}
                      of bel{' '}
                      <a href="tel:0647650537" className="text-primary hover:underline font-medium">
                        06 4765 0537
                      </a>.
                    </p>
                    <p className="text-xs text-foreground/50">
                      De Content Factory BV · Oude Middenweg 17, 2491 AC Den Haag · KvK 84512490
                    </p>
                  </div>
                </div>
              </motion.div>
            </main>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VoorwaardenPage;
